import {
  fetchBrokerSyncStatus,
  type BrokerSyncStatusResponse,
} from "../services/brokerIntegrationApi";
import { isApiRequestError } from "./apiRequestError";
import {
  notifyTradesUpdated,
  type TradesUpdatedDetail,
} from "../hooks/useTradeStatistics";

const DEFAULT_POLL_INTERVAL_MS = 1_500;
const DEFAULT_MAX_WAIT_MS = 600_000;
/** First sync often creates the trade account a beat after POST /broker/sync returns. */
const DEFAULT_ACCOUNT_NOT_FOUND_GRACE_MS = 45_000;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** True when sync-status says the trade account is not visible yet (common on first sync). */
export function isBrokerSyncStatusAccountNotFound(err: unknown): boolean {
  if (!isApiRequestError(err) || err.status !== 404) return false;
  return (
    err.slug === "broker.account_not_found" ||
    /trade account not found/i.test(err.message || "")
  );
}

export type WaitForBrokerAnalyticsReadyOptions = {
  accountCode: string;
  brokerName: string;
  pollIntervalMs?: number;
  maxWaitMs?: number;
  /**
   * How long to treat 404 as “account still provisioning” before failing.
   * Default 45s — covers first OANDA/API sync creating the trade account.
   */
  accountNotFoundGraceMs?: number;
  signal?: AbortSignal;
  onStatus?: (status: BrokerSyncStatusResponse) => void;
  /** Called when status 404s so the caller can refresh trade-accounts / resolve code. */
  onAccountNotFound?: () => Promise<string | null | void>;
};

/**
 * Poll GET /broker/sync-status until the backend phase exits `syncing`.
 * Use the canonical `account_code` from GET /broker/trade-accounts.
 * Transient 404s (account not created yet) are retried within the grace window.
 */
export async function waitForBrokerAnalyticsReady(
  options: WaitForBrokerAnalyticsReadyOptions
): Promise<BrokerSyncStatusResponse> {
  let accountCode = options.accountCode.trim();
  const brokerName = options.brokerName.trim().toLowerCase();
  if (!accountCode || !brokerName) {
    throw new Error("account_code and broker_name are required to poll sync status.");
  }

  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const deadline = Date.now() + (options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS);
  const accountNotFoundGraceMs =
    options.accountNotFoundGraceMs ?? DEFAULT_ACCOUNT_NOT_FOUND_GRACE_MS;
  const accountNotFoundDeadline = Date.now() + accountNotFoundGraceMs;
  let lastStatus: BrokerSyncStatusResponse | null = null;
  let sawAccountNotFound = false;

  while (Date.now() < deadline) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    try {
      const status = await fetchBrokerSyncStatus(accountCode, brokerName);
      lastStatus = status;
      options.onStatus?.(status);

      if (status.phase === "ready" || status.ready_for_analytics) {
        return status;
      }

      if (status.phase === "needs_instrument_setup") {
        return status;
      }
    } catch (err) {
      if (!isBrokerSyncStatusAccountNotFound(err)) {
        throw err;
      }
      sawAccountNotFound = true;
      if (Date.now() >= accountNotFoundDeadline) {
        throw err;
      }
      const refreshed = await options.onAccountNotFound?.();
      if (typeof refreshed === "string" && refreshed.trim()) {
        accountCode = refreshed.trim();
      }
    }

    const waitMs = Math.min(pollIntervalMs, Math.max(0, deadline - Date.now()));
    if (waitMs <= 0) break;
    await sleep(waitMs, options.signal);
  }

  if (sawAccountNotFound && !lastStatus) {
    throw new Error(
      `Timed out waiting for trade account ${accountCode} (${brokerName}) to appear after sync.`
    );
  }

  const suffix = lastStatus
    ? ` (phase=${lastStatus.phase || "unknown"}, inflight=${lastStatus.inflight}, closed_trades=${lastStatus.closed_trades})`
    : "";
  throw new Error(`Timed out waiting for trade sync to finish indexing${suffix}.`);
}

export type AfterBrokerResyncOptions = {
  /** Canonical Tessera account code; refetch may fill this when omitted. */
  accountCode?: string | null;
  brokerName: string;
  userId?: string;
  action?: TradesUpdatedDetail["action"];
  /** Reload trade accounts and return active account code when needed. */
  refetchBrokerAccounts?: () => Promise<string | null | void>;
  pollIntervalMs?: number;
  maxWaitMs?: number;
  accountNotFoundGraceMs?: number;
  signal?: AbortSignal;
};

/**
 * After POST/GET broker trade sync: poll sync-status, then invalidate caches and notify pages.
 */
export async function afterBrokerResyncWaitForAnalytics(
  options: AfterBrokerResyncOptions
): Promise<BrokerSyncStatusResponse> {
  const brokerName = options.brokerName.trim().toLowerCase();
  let accountCode = (options.accountCode || "").trim();

  const refetchedCode = await options.refetchBrokerAccounts?.();
  if (!accountCode && typeof refetchedCode === "string" && refetchedCode.trim()) {
    accountCode = refetchedCode.trim();
  }
  if (!accountCode) {
    throw new Error("Missing account code for sync status polling.");
  }

  const status = await waitForBrokerAnalyticsReady({
    accountCode,
    brokerName,
    pollIntervalMs: options.pollIntervalMs,
    maxWaitMs: options.maxWaitMs,
    accountNotFoundGraceMs: options.accountNotFoundGraceMs,
    signal: options.signal,
    onAccountNotFound: async () => {
      const again = await options.refetchBrokerAccounts?.();
      return typeof again === "string" ? again : null;
    },
  });

  if (status.phase === "ready" || status.ready_for_analytics) {
    notifyTradesUpdated({
      userId: options.userId,
      action: options.action ?? "sync",
      accountCode: status.account_code?.trim() || accountCode,
      brokerName,
      skipRefetchRetries: true,
    });
  }

  return status;
}
